import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { Link as RouterLink } from 'react-router-dom';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Controller, useForm } from 'react-hook-form';
import Alert from '@mui/material/Alert';

const CardHeader = ({ title, children }) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <div className='iconBadge'>
          {children}
        </div>
        <Typography variant="xl" fontWeight="medium" component="h3">
          {title}
        </Typography>
      </Box>
      <hr />
    </>
  );
};

const Admin = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const defaultValues = {
    desiredEmail: 'jane.doe@nativecoders.se',
    // Primary Emergency Contact
    primaryContactName: 'Jane Doe',
    primaryContactRelation: 'Spouse',
    primaryContactAddress: '123 Main Street',
    primaryContactEmail: 'jane.personal@gmail.com',
    primaryContactPhone: '+254700000000',
    // Secondary Emergency Contact
    secondaryContactName: 'Jane Doe',
    secondaryContactRelation: 'Spouse',
    secondaryContactAddress: '123 Main Street',
    secondaryContactEmail: 'jane.personal@gmail.com',
    secondaryContactPhone: '+254700000000',
    // Financial Information
    bankAccountNumber: '12345678901',
    clearingNumber: '1234',
    bankName: 'DNB Bank',
    bankAddress: 'Dronning Eufemias gate 30, 0191 Oslo',
    pensionAccount: 'NSSF123456789',
    voluntaryPensionContribution: 'Kes 2,500',
    personalIdNumber: '12345678901',
    kraPin: 'A123456789P',
    shifNumber: 'SHIF123456789',
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues,
  });

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    reset(defaultValues);
  };

  const onSubmit = async (data) => {
    try {
      setIsSaving(true);
      console.log('Form data:', data);
      // TODO: Add API call to save data
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Engagement Information (read-only, managed by HR)
  const engagementData = {
    startDate: '2023-01-15',
    hiredPosition: 'Senior Software Developer',
    engagementType: 'Subcontractor',
    affiliation: 'External',
    positionType: 'Consultant',
    organizationalUnit: 'Development Team',
    company: 'Native Coders AS',
    workLocation: 'Oslo',
    directReports: ['Alice Johnson', 'Bob Smith'],
    peopleManager: 'Sarah Wilson',
    responsibleSalesManager: 'Mike Johnson',
  };

  return (
    <main className="main-section">
      <div className="content-section nc-profile">
        <RouterLink to="/" className="nc-back-link">
          <ArrowBackIcon />
          <Typography variant="md" component="span">Go Home</Typography>
        </RouterLink>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="displaySm" component="h1">Engagement & Admin</Typography>
          {!isEditMode ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              sx={{
                textTransform: 'none',
                borderColor: '#483C3B',
                color: '#483C3B',
                '&:hover': {
                  borderColor: '#483C3B',
                  backgroundColor: 'rgba(72, 60, 59, 0.04)',
                },
              }}
            >
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit(onSubmit)}
                disabled={isSaving}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E84610',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#d63d0e',
                  },
                }}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
                sx={{
                  textTransform: 'none',
                  borderColor: '#EAE7E6',
                  color: '#483C3B',
                  backgroundColor: '#EAE7E6',
                  '&:hover': {
                    borderColor: '#EAE7E6',
                    backgroundColor: '#d5d2d1',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        {/* Personal Information Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent>
            <CardHeader title="Personal Information">
              <PersonOutlineIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Desired Native Coders Email</Typography>
                  <Typography variant="sm" component="p" sx={{ mb: 1, color: 'text.secondary' }}>
                    Use your first name and last name; firstname.lastname@companydomain.com e.g. jane.doe@nativecoders.se
                  </Typography>
                  <Typography variant="md" component="p">jane.doe@nativecoders.se</Typography>
                </div>
              ) : (
                <Controller
                  name="desiredEmail"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Desired Native Coders Email"
                      size="small"
                      helperText="Use your first name and last name; firstname.lastname@companydomain.com e.g. jane.doe@nativecoders.se"
                    />
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Engagement Information Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent>
            <CardHeader title="Engagement Information">
              <DescriptionIcon />
            </CardHeader>
            <div className="nc-card-body">
              <Alert 
                icon={<InfoOutlinedIcon />} 
                severity="info" 
                sx={{ mb: 2 }}
              >
                This information is managed by HR and cannot be edited.
              </Alert>
              <div className="nc-grid-2">
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Start Date</Typography>
                  <Typography variant="md" component="p">{engagementData.startDate}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Hired Position</Typography>
                  <Typography variant="md" component="p">{engagementData.hiredPosition}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Engagement Type</Typography>
                  <Typography variant="md" component="p">{engagementData.engagementType}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Affiliation</Typography>
                  <Typography variant="md" component="p">{engagementData.affiliation}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Position Type</Typography>
                  <Typography variant="md" component="p">{engagementData.positionType}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Organizational Unit</Typography>
                  <Typography variant="md" component="p">{engagementData.organizationalUnit}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Company</Typography>
                  <Typography variant="md" component="p">{engagementData.company}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Work Location</Typography>
                  <Typography variant="md" component="p">{engagementData.workLocation}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Direct Reports</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {engagementData.directReports.map((report) => (
                      <Chip key={report} label={report} sx={{ backgroundColor: '#EAE7E6' }} />
                    ))}
                  </Box>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">People Manager</Typography>
                  <Typography variant="md" component="p">{engagementData.peopleManager}</Typography>
                </div>
                <div>
                  <Typography variant="sm" fontWeight="light" component="p">Responsible Sales Manager</Typography>
                  <Typography variant="md" component="p">{engagementData.responsibleSalesManager}</Typography>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Information Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent>
            <CardHeader title="Financial Information">
              <CreditCardIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <div className="nc-grid-2">
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Bank Account Number</Typography>
                    <Typography variant="md" component="p">12345678901</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Clearing Number</Typography>
                    <Typography variant="md" component="p">1234</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Bank Name</Typography>
                    <Typography variant="md" component="p">DNB Bank</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Bank Address</Typography>
                    <Typography variant="md" component="p">Dronning Eufemias gate 30, 0191 Oslo</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Pension Account (NSSF)</Typography>
                    <Typography variant="md" component="p">NSSF123456789</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Voluntary Pension Contribution (Monthly)</Typography>
                    <Typography variant="md" component="p">Kes 2,500</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Personal ID Number</Typography>
                    <Typography variant="md" component="p">12345678901</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">KRA PIN</Typography>
                    <Typography variant="md" component="p">A123456789P</Typography>
                  </div>
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">SHIF Number</Typography>
                    <Typography variant="md" component="p">SHIF123456789</Typography>
                  </div>
                </div>
              ) : (
                <div className="nc-grid-2">
                  <Controller
                    name="bankAccountNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Bank Account Number"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="clearingNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Clearing Number"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="bankName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Bank Name"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="bankAddress"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Bank Address"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="pensionAccount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Pension Account (NSSF)"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="voluntaryPensionContribution"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Voluntary Pension Contribution (Monthly)"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="personalIdNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Personal ID Number"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="kraPin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="KRA PIN"
                        size="small"
                      />
                    )}
                  />
                  <Controller
                    name="shifNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SHIF Number"
                        size="small"
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contacts Section */}
        <Card id="profile-info-card" sx={{ mb: 2 }}>
          <CardContent>
            <CardHeader title="Emergency Contacts">
              <WarningAmberIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="md" component="p" sx={{ mb: 2, fontWeight: 'medium' }}>
                      Primary Emergency Contact
                    </Typography>
                    <div className="nc-grid-2">
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Full Name</Typography>
                        <Typography variant="md" component="p">Jane Doe</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Relation</Typography>
                        <Typography variant="md" component="p">Spouse</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Address</Typography>
                        <Typography variant="md" component="p">123 Main Street</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Email</Typography>
                        <Typography variant="md" component="p">jane.personal@gmail.com</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Phone</Typography>
                        <Typography variant="md" component="p">+254700000000</Typography>
                      </div>
                    </div>
                  </Box>
                  <Box sx={{ pt: 2, borderTop: '1px solid #EAE7E6' }}>
                    <Typography variant="md" component="p" sx={{ mb: 2, fontWeight: 'medium' }}>
                      Secondary Emergency Contact
                    </Typography>
                    <div className="nc-grid-2">
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Full Name</Typography>
                        <Typography variant="md" component="p">Jane Doe</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Relation</Typography>
                        <Typography variant="md" component="p">Spouse</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Address</Typography>
                        <Typography variant="md" component="p">123 Main Street</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Email</Typography>
                        <Typography variant="md" component="p">jane.personal@gmail.com</Typography>
                      </div>
                      <div>
                        <Typography variant="sm" fontWeight="light" component="p">Phone</Typography>
                        <Typography variant="md" component="p">+254700000000</Typography>
                      </div>
                    </div>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="md" component="p" sx={{ mb: 2, fontWeight: 'medium' }}>
                      Primary Emergency Contact
                    </Typography>
                    <div className="nc-grid-2">
                      <Controller
                        name="primaryContactName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Full Name"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="primaryContactRelation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Relation"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="primaryContactAddress"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Address"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="primaryContactEmail"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email"
                            type="email"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="primaryContactPhone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone"
                            size="small"
                          />
                        )}
                      />
                    </div>
                  </Box>
                  <Box sx={{ pt: 2, borderTop: '1px solid #EAE7E6' }}>
                    <Typography variant="md" component="p" sx={{ mb: 2, fontWeight: 'medium' }}>
                      Secondary Emergency Contact
                    </Typography>
                    <div className="nc-grid-2">
                      <Controller
                        name="secondaryContactName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Full Name"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="secondaryContactRelation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Relation"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="secondaryContactAddress"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Address"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="secondaryContactEmail"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email"
                            type="email"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        name="secondaryContactPhone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone"
                            size="small"
                          />
                        )}
                      />
                    </div>
                  </Box>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Admin;

